import {
  AB_SPLIT_ACTION_LABEL,
  INFINITY_TESTING_ACTION_LABEL,
  OPTIMIZED_AB_SPLIT_ACTION_LABEL,
  RICH_MESSAGE_ACTION_LABEL,
  SEND_EMAIL_ACTION_LABEL,
  SEND_MESSAGE_ACTION_LABEL,
  SUBSCRIBER_ATTRIBUTE_SPLIT_ACTION_LABEL,
  SUBSCRIBER_EVENT_SPLIT_ACTION_LABEL,
  TRIGGER_EVENT_SPLIT_ACTION_LABEL,
} from 'components/flowBuilder/constants';
import { StaticActionsTypes } from 'components/flowBuilder/constants/actions';

// Single source of truth for which action types support custom titles, plus
// each type's default label.
export const NAMEABLE_ACTION_LABELS: Readonly<Record<string, string>> = {
  [StaticActionsTypes.SEND_MESSAGE]: SEND_MESSAGE_ACTION_LABEL,
  [StaticActionsTypes.SEND_EMAIL]: SEND_EMAIL_ACTION_LABEL,
  [StaticActionsTypes.RICH_MESSAGE]: RICH_MESSAGE_ACTION_LABEL,
  [StaticActionsTypes.AB_SPLIT]: AB_SPLIT_ACTION_LABEL,
  [StaticActionsTypes.OPTIMIZED_AB_SPLIT]: OPTIMIZED_AB_SPLIT_ACTION_LABEL,
  [StaticActionsTypes.WAIT_FOR_EVENT_SPLIT]:
    SUBSCRIBER_EVENT_SPLIT_ACTION_LABEL,
  [StaticActionsTypes.TRIGGER_EVENT_SPLIT]: TRIGGER_EVENT_SPLIT_ACTION_LABEL,
  [StaticActionsTypes.SUBSCRIBER_ATTRIBUTE_SPLIT]:
    SUBSCRIBER_ATTRIBUTE_SPLIT_ACTION_LABEL,
  [StaticActionsTypes.AI_MANAGED_MESSAGE]: INFINITY_TESTING_ACTION_LABEL,
};

export const isNameableActionType = (type: string): boolean =>
  Object.prototype.hasOwnProperty.call(NAMEABLE_ACTION_LABELS, type);

interface ActionTitleAndSubtitle {
  title: string;
  subtitle: string | undefined;
}

export const getActionTitleAndSubtitle = (
  hasActionTitles: boolean,
  customTitle: string | undefined,
  defaultLabel: string,
): ActionTitleAndSubtitle => ({
  title: hasActionTitles && customTitle ? customTitle : defaultLabel,
  subtitle: hasActionTitles && customTitle ? defaultLabel : undefined,
});

// Escapes regex metacharacters so an action label can be safely embedded as a
// literal in a RegExp pattern. Defends against labels containing characters
// like "/" or "(" — e.g. "A/B Split".
const escapeRegExp = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Builds the regex used to detect/parse auto-numbered titles for a given
 * action type, e.g. /^Message #(\d+)$/. Returns null if the type doesn't
 * support custom titles. Single source of truth for the auto-numbered title
 * format — consumed by both the duplicate-suffix path
 * (`getDuplicatedActionCustomTitle`) and the auto-numbering walk
 * (`scanExistingTitles`), so the two never drift on what counts as
 * "auto-numbered".
 */
export const buildAutoNumberedTitleRegex = (type: string): RegExp | null => {
  const label = NAMEABLE_ACTION_LABELS[type];
  if (!label) return null;
  return new RegExp(`^${escapeRegExp(label)} #(\\d+)$`);
};

export const matchesAutoNumberedPattern = (
  title: string,
  type: string,
): boolean => {
  const re = buildAutoNumberedTitleRegex(type);
  return re !== null && re.test(title);
};

// Computes the customTitle for a duplicated action.
//   - undefined / empty / non-string -> undefined (auto-numbering walk assigns a number)
//   - auto-numbered for this type    -> undefined (auto-numbering walk assigns the next number)
//   - custom                         -> "<original> (copy)" (compounds on successive duplicates)
//
// Accepts `unknown` so it can read `customTitle` off of dynamic action params,
// whose type is `Record<string, unknown>`.
export const getDuplicatedActionCustomTitle = (
  originalTitle: unknown,
  type: string,
): string | undefined => {
  if (typeof originalTitle !== 'string' || !originalTitle) return undefined;
  if (matchesAutoNumberedPattern(originalTitle, type)) return undefined;
  return `${originalTitle} (copy)`;
};
